import { SortTermActive } from "@/composables/Sort/SortTerms";
import { BestandsaufnahmeJson } from "@/models/ba/interfaces/IBestandsaufnahme";
import User from "@/models/user";
import { logger } from '@/utilities/logging';
import { Model } from '@vuex-orm/core';
import Dokument from './dokument.model';
import ErweitertesMedium from './erweitertes-medium.model';
import StrapiMedia from './photo/strapi-media.model';

export default class Immobilie extends Model {

  static entity = 'immobilie';

  static fields() {
    return {
      id: this.attr(null),
      name: this.attr(null),
      eigentuemer: this.attr(null),
      strasse: this.attr(null),
      plz: this.attr(null),
      stadt: this.attr(null),
      stadtbezirk: this.attr(null),
      verwalters: this.attr(null),
      externeObjektNr: this.attr(null),
      technikzentraleBericht: this.attr(null),
      baujahr: this.attr(null),
      bilder: this.attr(null),
      vorschaubild: this.attr(null),
      videos: this.attr(null),
      drohnenbilder: this.attr(null),
      imageVideo: this.attr(null),
      imageVideoLink: this.attr(null),
      anlageschema: this.attr(null),
      geolocationLat: this.attr(null),
      geolocationLon: this.attr(null),
      grundrisss: this.attr(null),
      matterports: this.attr(null),
      dokuments: this.attr(null),
      updatedAt: this.attr(null),
      isDownloaded: this.attr(null),
      bestandsaufnahmes: this.attr(null),
      grundrissAnzeigen: this.attr(null),
      drohnenbilderAnzeigen: this.attr(null),
      matterportsAnzeigen: this.attr(null),
      managementSummaries: this.attr(null),
      otherDokuments: this.attr(null),
      gewerbeeinheiten: this.attr(null),
      wohneinheiten: this.attr(null)
    };
  }


  id!: number;
  name!: string;
  eigentuemer!: string;
  strasse!: string;
  plz!: string;
  stadt!: string;
  stadtbezirk!: string;
  verwalters!: User[];
  baujahr!: number;
  externeObjektNr!: string;
  technikzentraleBericht!: StrapiMedia;
  bilder!: StrapiMedia[];
  vorschaubild!: StrapiMedia;
  videos!: StrapiMedia[];
  drohnenbilder!: StrapiMedia[]
  imageVideo!: StrapiMedia;
  imageVideoLink!: string;
  anlageschema!: StrapiMedia;
  geolocationLat!: number;
  geolocationLon!: number;
  grundrisss!: ErweitertesMedium[];
  matterports!: ErweitertesMedium[];
  dokuments!: Dokument[];
  managementSummaries!: Dokument[];
  otherDokuments!: Dokument[];

  bestandsaufnahmes!: BestandsaufnahmeJson[];
  updatedAt!: Date;
  isDownloaded!: boolean;
  grundrissAnzeigen!: boolean;
  drohnenbilderAnzeigen!: boolean;
  matterportsAnzeigen!: boolean;
  gewerbeeinheiten!: number;
  wohneinheiten!: number;
}


export const immobilie = {
  state: {
    propertiesLoaded: false,
    pageSize: 10,
    page: 0,
    pageCount: undefined,
    sortBy: { label: 'Ext. Objektnummer', fieldName: 'externeObjektNr', orderBy: 'asc'},
    searchTerm: undefined,

    persistedPropertiesLoaded: false,
    persistedProperties: undefined,

    fallbackProperties: [],
  },
  actions: {
    setLoaded: ({ state }: { state: any}) => {
      state.propertiesLoaded = true;
    },
    setPage: ({ state }: { state: any}, page: number) => {
      state.page = page;
    },
    setPageCount: ({ state }: { state: any}, pageCount: number) => {
      state.pageCount = pageCount;
    },
    setSortBy: ({ state }: { state: any}, sortBy: SortTermActive) => {
      state.sortBy = sortBy;
    },
    setSearchTerm: ({ state }: { state: any}, searchTerm: string) => {
      state.searchTerm = searchTerm;
    },

    loadPersistedProperties: async ({ state }: { state: any}) => {
      if (!state.persistedPropertiesLoaded) {
        const {immobilie} = await Immobilie.dispatch("$fetchFromLocal");
        await Immobilie.deleteAll();
        state.persistedProperties = immobilie;
        state.persistedPropertiesLoaded = true;
        return immobilie;
      } else {
        return state.persistedProperties || [];
      }
    },
    addToPersistedProperties: async({ state }: { state: any}, immobilie: Immobilie) => {
      if (state.persistedPropertiesLoaded) {

        if (!state.persistedProperties) { state.persistedProperties = [] }
        state.persistedProperties.push(immobilie);
      }
    },
    removeFromPersistedProperties: async({ state }: { state: any}, immobilie: Immobilie) => {
      if (state.persistedPropertiesLoaded && state.persistedProperties) {
        const index = state.persistedProperties.findIndex((el: Immobilie) => el.id === immobilie.id);
        if (index >= 0) {
          state.persistedProperties.splice(index, 1);
        } else {
          console.warn("Could not remove property from persisted properties, index is ", index);
          logger.warn(`Could not remove property from persisted properties, index is ${index}`);

        }
      }
    },

    /**
     * When ba's gets loaded, we get information of properties within.
     * But we cannot store those properties to the store of the other properties as this would bring confusion to the pagination but also filter system.
     * That is why we add those properties to a separate storage.
     */
    addToFallbackProperties: async({ state }: { state: any}, immobilie: any) => {
      if (!immobilie) { return; }

      if (!state.fallbackProperties.find((el: any) => el.id === immobilie.id) ){
        state.fallbackProperties.push(immobilie);
      }
    },
  },
  getters: {
    loaded: (state: any) => state.propertiesLoaded,
    page: (state: any) => state.page,
    pageCount: (state: any) => state.pageCount,
    sortBy: (state: any) => state.sortBy,
    searchTerm: (state: any) => state.searchTerm,
    persistedProperties: (state: any) => state.persistedProperties || [],

    propMatchesFilter: () => (property: Immobilie, searchTerm: string) => {
      return property.externeObjektNr?.toLowerCase().includes(searchTerm) ||
      property.strasse?.toLowerCase().includes(searchTerm) ||
      property.stadt?.toLowerCase().includes(searchTerm) ||
      property.plz?.toLowerCase().includes(searchTerm) ||
      property.name?.toLowerCase().includes(searchTerm) ||
      property.eigentuemer?.toLowerCase().includes(searchTerm) ||
      property.baujahr.toString().toLowerCase().includes(searchTerm);
    },
    getFallbackProperties: ( state: any) => {
      return state.fallbackProperties;
    },
    getPropOrFallbackProp: (state: any) => (id: number) => {
      const prop = Immobilie.find(id)
      if (prop) { return prop }

      return state.fallbackProperties.find((el: any) => el.id === id);
    }
  }
}